import { format, isValid } from "date-fns";

// ---------------------------------------------------------------------------
// Wire-boundary helpers
// ---------------------------------------------------------------------------
//
// HTML's `<input type="date">` and `<input type="datetime-local">` emit naive
// local strings with NO timezone suffix. Sending those raw to a .NET backend
// that deserialises into `DateTimeOffset` parses them using the BE process's
// local offset (UTC on most container hosts), silently misinterpreting the
// user's wall-clock as UTC and shifting the stored timestamp by the user's
// offset (e.g. losing the first hour of the local day during BST). These
// helpers bridge cleanly between the input shapes and full ISO-8601 UTC.
//
// All are pure and timezone-aware via the runtime `Date` object.

/**
 * Normalises a date-only value from `<input type="date">` (`yyyy-MM-dd`) into a
 * canonical UTC ISO-8601 timestamp at midnight UTC, e.g.
 * `2026-07-14` → `2026-07-14T00:00:00.000Z`. Use at the API-payload boundary
 * for fields the BE types as `DateTimeOffset` — a bare date-only string risks
 * ambiguous/failed deserialisation. A value that is already a full ISO
 * timestamp is passed through unchanged. Empty/invalid → `undefined` so callers
 * omit the field (BE clears it).
 */
export function dateOnlyToIsoUtc(value: string | null | undefined): string | undefined {
  if (!value) return undefined;
  const parsed = value.includes("T") ? new Date(value) : new Date(`${value}T00:00:00Z`);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed.toISOString();
}

/**
 * Returns the current local wall-clock as a `yyyy-MM-ddTHH:mm` string — the
 * format an `<input type="datetime-local">` element expects. Minute precision;
 * seconds and timezone deliberately omitted.
 */
export function nowLocalDateTimeInputValue(): string {
  return format(new Date(), "yyyy-MM-dd'T'HH:mm");
}

/**
 * Converts a naive local-time string from `<input type="datetime-local">`
 * (shape `yyyy-MM-ddTHH:mm` or `yyyy-MM-ddTHH:mm:ss`) into a full ISO-8601 UTC
 * string with `Z` suffix that .NET `DateTimeOffset` cannot ambiguously
 * interpret. Empty / undefined inputs pass through as `undefined`.
 */
export function localDateTimeInputToIsoUtc(value: string | null | undefined): string | undefined {
  if (!value) return undefined;
  const parsed = new Date(value);
  if (!isValid(parsed)) return undefined;
  return parsed.toISOString();
}

/**
 * Ensures an ISO-8601 string is treated as UTC by appending a `Z` suffix when
 * no explicit offset or UTC indicator is present. The backend may emit
 * timestamps like `2026-05-26T06:21:00` (no Z), which JS would otherwise parse
 * as local time.
 */
export function ensureUtcIso(value: string): string {
  return /[Zz]|[+-]\d{2}:?\d{2}$/.test(value) ? value : value + "Z";
}

/**
 * Converts an ISO-8601 string (with or without explicit offset) into the naive
 * local-time shape `yyyy-MM-ddTHH:mm` expected by `<input type="datetime-local">`.
 * Returns the supplied fallback (default empty string) when the input is
 * missing or unparseable. When `value` carries no offset or `Z` suffix it is
 * treated as UTC before converting to local time, so the edit form displays the
 * correct time regardless of the user's timezone.
 */
export function isoUtcToLocalDateTimeInput(value: string | null | undefined, fallback = ""): string {
  if (!value) return fallback;
  const parsed = new Date(ensureUtcIso(value));
  if (!isValid(parsed)) return fallback;
  return format(parsed, "yyyy-MM-dd'T'HH:mm");
}
